import { useState, useEffect, useCallback, useRef, type MouseEvent } from 'react';
import { doubleTapProps } from '@/shared/device/doubleTap';
import { createPortal } from 'react-dom';
import type { CellAttachment } from './types';
import { isImageAttachment, isPdfAttachment, formatFileSize } from './types';
import { useI18n } from '@/shared/i18n/useT';

export interface MediaLightboxModalProps {
  readonly attachments: readonly CellAttachment[];
  readonly initialIndex?: number | undefined;
  readonly onClose: () => void;
  readonly onDelete?: ((index: number) => void) | undefined;
}

export function MediaLightboxModal({
  attachments,
  initialIndex = 0,
  onClose,
  onDelete,
}: MediaLightboxModalProps) {
  const { t } = useI18n();
  const [index, setIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  const current = attachments[index] ?? attachments[0];
  const hasMultiple = attachments.length > 1;

  // Reset transforms on attachment change
  useEffect(() => {
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  }, [index]);

  const handlePrev = useCallback(() => {
    setIndex((prev) => (prev > 0 ? prev - 1 : attachments.length - 1));
  }, [attachments.length]);

  const handleNext = useCallback(() => {
    setIndex((prev) => (prev < attachments.length - 1 ? prev + 1 : 0));
  }, [attachments.length]);

  const handleZoomIn = () => setZoom((z) => Math.min(5, Number((z + 0.25).toFixed(2))));
  const handleZoomOut = () => setZoom((z) => Math.max(0.25, Number((z - 0.25).toFixed(2))));
  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };
  const handleRotate = () => setRotation((r) => (r + 90) % 360);

  // Keyboard navigation
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') handlePrev();
      else if (e.key === 'ArrowRight') handleNext();
      else if (e.key === '+' || e.key === '=') handleZoomIn();
      else if (e.key === '-') handleZoomOut();
      else if (e.key === '0') handleResetZoom();
      else if (e.key.toLowerCase() === 'r') handleRotate();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleNext, handlePrev, onClose]);

  if (!current) return null;

  const isImg = isImageAttachment(current);
  const isPdf = isPdfAttachment(current);

  // Mouse pan handlers
  const handleMouseDown = (e: MouseEvent) => {
    if (zoom <= 1) return;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setPan({ x: dragStartRef.current.panX + dx, y: dragStartRef.current.panY + dy });
  };

  const handleMouseUp = () => setIsDragging(false);

  const modalContent = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={current.name}
      className="fixed inset-0 z-50 flex flex-col bg-black/90 backdrop-blur-md select-none"
      onClick={onClose}
    >
      {/* Top Controls Bar */}
      <div
        className="flex h-12 w-full shrink-0 items-center justify-between border-b border-border bg-surface px-4 text-fg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex min-w-0 items-center gap-3">
          <span className="truncate text-sm font-semibold">{current.name}</span>
          {current.size > 0 && (
            <span className="text-xs text-muted">{`(${formatFileSize(current.size)})`}</span>
          )}
          {hasMultiple && (
            <span className="rounded-full bg-surface-3 px-2 py-0.5 text-xs text-muted font-medium">
              {`${index + 1} / ${attachments.length}`}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1 sm:gap-2">
          {/* Zoom controls for images */}
          {isImg && (
            <>
              <button
                type="button"
                onClick={handleZoomOut}
                disabled={zoom <= 0.25}
                title={t('attachments.lightbox.zoomOut')}
                aria-label={t('attachments.lightbox.zoomOut')}
                className="tap-target flex h-8 w-8 items-center justify-center rounded-full hover:bg-surface-3 disabled:opacity-40"
              >
                −
              </button>
              <button
                type="button"
                onClick={handleResetZoom}
                title={t('attachments.lightbox.resetZoom')}
                aria-label={t('attachments.lightbox.resetZoom')}
                className="px-1.5 py-0.5 text-xs font-mono font-medium rounded hover:bg-surface-3"
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                type="button"
                onClick={handleZoomIn}
                disabled={zoom >= 5}
                title={t('attachments.lightbox.zoomIn')}
                aria-label={t('attachments.lightbox.zoomIn')}
                className="tap-target flex h-8 w-8 items-center justify-center rounded-full hover:bg-surface-3 disabled:opacity-40"
              >
                +
              </button>
              <button
                type="button"
                onClick={handleRotate}
                title={t('attachments.lightbox.rotate')}
                aria-label={t('attachments.lightbox.rotate')}
                className="tap-target flex h-8 w-8 items-center justify-center rounded-full hover:bg-surface-3"
              >
                ↻
              </button>
              <div className="mx-1 h-4 w-px bg-border" />
            </>
          )}

          {/* Download button */}
          <a
            href={current.url}
            download={current.name}
            target="_blank"
            rel="noopener noreferrer"
            title={t('attachments.download')}
            aria-label={t('attachments.download')}
            className="tap-target flex h-8 w-8 items-center justify-center rounded-full hover:bg-surface-3 text-fg"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
          </a>

          {/* Delete attachment button (if editable) */}
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(index)}
              title={t('attachments.delete')}
              aria-label={t('attachments.delete')}
              className="tap-target flex h-8 w-8 items-center justify-center rounded-full text-danger hover:bg-danger/10"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          )}

          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            title={t('attachments.lightbox.close')}
            aria-label={t('attachments.lightbox.close')}
            className="tap-target flex h-8 w-8 items-center justify-center rounded-full hover:bg-surface-3 text-fg ml-1"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Main Preview Area */}
      <div
        className="relative flex flex-1 items-center justify-center overflow-hidden p-2 sm:p-6"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        {isImg ? (
          <div
            className="transition-transform duration-100 ease-out"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
              cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
            }}
            onClick={(e) => e.stopPropagation()}
            {...doubleTapProps(() => (zoom === 1 ? setZoom(2) : handleResetZoom()))}
          >
            <img
              src={current.url}
              alt={current.name}
              className="max-h-[82vh] max-w-[90vw] object-contain shadow-2xl rounded-sm"
              draggable={false}
            />
          </div>
        ) : isPdf ? (
          <div
            className="h-full w-full max-w-5xl rounded border border-border bg-surface shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={`${current.url}#view=FitH`}
              title={current.name}
              className="h-full w-full border-0"
            />
          </div>
        ) : (
          <div
            className="flex flex-col items-center justify-center rounded border border-border bg-surface p-8 text-center shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-sm font-semibold">{current.name}</span>
            <a
              href={current.url}
              download={current.name}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-accent px-4 py-2 text-sm font-semibold text-accent-fg"
            >
              {t('attachments.download')}
            </a>
          </div>
        )}

        {/* Previous & Next Buttons */}
        {hasMultiple && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handlePrev();
              }}
              title={t('attachments.lightbox.prev')}
              aria-label={t('attachments.lightbox.prev')}
              className="tap-target absolute left-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-surface/80 text-fg shadow-md backdrop-blur-sm hover:bg-surface"
            >
              ❮
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
              title={t('attachments.lightbox.next')}
              aria-label={t('attachments.lightbox.next')}
              className="tap-target absolute right-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-surface/80 text-fg shadow-md backdrop-blur-sm hover:bg-surface"
            >
              ❯
            </button>
          </>
        )}
      </div>

      {/* Bottom Thumbnail Filmstrip */}
      {hasMultiple && (
        <div
          className="flex h-16 w-full shrink-0 items-center justify-center gap-2 border-t border-border bg-surface px-4 overflow-x-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {attachments.map((att, idx) => {
            const isSelected = idx === index;
            return (
              <button
                key={att.id}
                type="button"
                onClick={() => setIndex(idx)}
                aria-label={att.name}
                className={`relative h-12 w-12 shrink-0 overflow-hidden rounded border transition-all ${
                  isSelected ? 'border-brand ring-2 ring-brand ring-offset-1' : 'border-border/80 opacity-70 hover:opacity-100'
                }`}
              >
                {isImageAttachment(att) ? (
                  <img src={att.thumbnail || att.url} alt={att.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-surface-2 text-[10px] font-bold">
                    {isPdfAttachment(att) ? 'PDF' : 'DOC'}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
}
